import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, TrendingUp, Clock, AlertCircle } from "lucide-react";
import { AIService, AIAnalysis } from '@/services/ai/AIService';
import { Incident, CatalogTask } from '@/types/dashboard';
import { format } from 'date-fns';

interface AIPredictiveAnalysisProps {
  incidents: Incident[];
  catalogTasks: CatalogTask[];
  startDate: Date;
  endDate: Date;
}

export function AIPredictiveAnalysis({ 
  incidents, 
  catalogTasks, 
  startDate, 
  endDate 
}: AIPredictiveAnalysisProps) {
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        const aiService = AIService.getInstance();
        const result = await aiService.analyzeIncidents(incidents);
        setAnalysis(result);
        setError(null);
      } catch (err) {
        setError('Failed to analyze data. Using fallback analysis.');
        console.error('Error in AI analysis:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [incidents, catalogTasks, startDate, endDate]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            Analyzing data with AI...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Analysis Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Predicted Incidents (Next Week)
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analysis.predictions.nextWeekIncidents}
            </div>
            <p className="text-xs text-muted-foreground">
              Based on historical patterns and current trends
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              System Risk Level
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analysis.recommendations.deployment.riskLevel}
            </div>
            <p className="text-xs text-muted-foreground">
              Current system risk assessment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Optimal Deployment Time
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {format(analysis.recommendations.deployment.optimalTiming, 'MMM d, h:mm a')}
            </div>
            <p className="text-xs text-muted-foreground">
              Recommended deployment window
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>High Risk Areas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analysis.predictions.highRiskAreas.map((area, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium">{area}</h4>
                </div>
                <Badge variant="destructive">High Risk</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recommended Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analysis.recommendations.system.preventiveActions.map((action, index) => (
              <Alert key={index}>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Action Required</AlertTitle>
                <AlertDescription>{action}</AlertDescription>
              </Alert>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pattern Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Common Root Causes</h4>
              <ul className="list-disc list-inside space-y-1">
                {analysis.patterns.commonRootCauses.map((cause, index) => (
                  <li key={index} className="text-sm text-gray-600">{cause}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Recurring Issues</h4>
              <ul className="list-disc list-inside space-y-1">
                {analysis.patterns.recurringIssues.map((issue, index) => (
                  <li key={index} className="text-sm text-gray-600">{issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 